import React, { useEffect, useState } from "react";
import { useRefreshTick } from "@/lib/autoRefresh";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, formatDate, ROLE_LABELS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportDialog } from "@/components/ExportDialog";
import { DashboardModules } from "@/components/DashboardModules";
import { ActionNeeded } from "@/components/ActionNeeded";
import { ExpenseSummaryPanel } from "@/components/ExpenseSummaryPanel";
import { VehicleStatusDonut } from "@/components/VehicleStatusDonut";
import { CompanyBillsPanel } from "@/components/CompanyBillsPanel";
import { ReceivablesDuePanel } from "@/components/ReceivablesDuePanel";
import { PemohonRevenuePanel } from "@/components/PemohonRevenuePanel";
import { CashBalancesPanel } from "@/components/CashBalancesPanel";
import { DashboardDepositSummary } from "@/components/DepositSummary";
import {
  FileText, CalendarDays, Clock, Wallet, Download, AlertTriangle, ArrowRight, TrendingUp, CreditCard, ChevronRight,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
function monthLabel(ym) {
  const [y, m] = (ym || "").split("-");
  return `${MONTH_ABBR[parseInt(m, 10) - 1] || ""} ${(y || "").slice(2)}`;
}

function StatCard({ icon: Icon, label, value, sub, accent, onClick }) {
  return (
    <Card onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`p-4 border-border/60 transition-all ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-brand/40" : ""}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </div>
      <p className="text-sm font-semibold text-foreground truncate">{label}</p>
      <p className="text-xl font-bold text-brand leading-tight mt-0.5" style={{ fontFamily: "Outfit" }}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [balance, setBalance] = useState(null);
  const [credit, setCredit] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("all");

  const canFilterBranch = ["direktur", "tim_keuangan"].includes(user?.role);
  const tick = useRefreshTick();

  useEffect(() => {
    const params = canFilterBranch && branch !== "all" ? { branch } : {};
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/pending").then((r) => setPending(r.data)).catch(() => {});
    if (["pemohon", "tim_kendaraan"].includes(user?.role)) api.get("/my-balance").then((r) => setBalance(r.data)).catch(() => {});
  }, [user, branch, tick]);

  useEffect(() => {
    if (canFilterBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [canFilterBranch]);

  useEffect(() => {
    if (["direktur", "it_admin", "tim_keuangan"].includes(user?.role)) api.get("/dashboard/credit-summary").then((r) => setCredit(r.data)).catch(() => {});
  }, [user, tick]);

  const canExport = ["it_admin", "direktur", "tim_keuangan"].includes(user?.role);
  const canRequestCash = ["pemohon", "tim_kendaraan"].includes(user?.role);
  const isFinance = ["direktur", "tim_keuangan", "it_admin"].includes(user?.role);
  const showDonut = ["direktur", "it_admin", "tim_kendaraan"].includes(user?.role);
  const rightCol = isFinance || showDonut;
  const showBranch = ["direktur", "tim_keuangan", "it_admin", "approver"].includes(user?.role) && stats?.branch_summary?.length > 0;
  const showCredit = !!credit && (credit.count_kredit > 0 || credit.count_lunas > 0);
  const showBills = ["staf_tagihan", "direktur", "tim_keuangan", "it_admin"].includes(user?.role);
  const showVeh = ["tim_kendaraan", "direktur", "it_admin"].includes(user?.role);
  const tabs = [
    { v: "pengajuan", l: "Pengajuan & Kas Berjalan" },
    showBranch && { v: "cabang", l: "Rekap Cabang" },
    showBills && { v: "tagihan", l: "Tagihan Vendor" },
    showVeh && { v: "kendaraan", l: "Kendaraan" },
    showCredit && { v: "kredit", l: "Kredit Kendaraan" },
  ].filter(Boolean);
  const pemohonSales = user?.role === "pemohon" && user?.sales_enabled;
  const statGrid = (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={CalendarDays} label="Pengajuan Hari Ini" value={stats?.today_count ?? "-"} accent="bg-emerald-100 text-emerald-700" onClick={() => navigate("/requests")} />
        <StatCard icon={FileText} label="Pengajuan Bulan Ini" value={stats?.month_count ?? "-"} sub={stats ? formatRupiah(stats.total_requested_month) : ""} accent="bg-sky-100 text-sky-700" onClick={() => navigate("/requests")} />
        <StatCard icon={Clock} label="Menunggu Approval" value={stats?.pending_count ?? "-"} accent="bg-amber-100 text-amber-700" onClick={() => navigate(["approver", "direktur"].includes(user?.role) ? "/approvals" : "/requests")} />
        <StatCard icon={Wallet} label="Kas Berjalan" value={stats ? formatRupiah(stats.total_outstanding) : "-"} sub="Belum dipertanggungjawabkan" accent="bg-rose-100 text-rose-700" onClick={() => navigate(canRequestCash ? "/expenses" : "/requests")} />
      </div>
  );
  const defaultTab = user?.role === "tim_kendaraan" ? "kendaraan" : user?.role === "staf_tagihan" ? "tagihan" : "pengajuan";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Dashboard</h2>
          <p className="text-xs text-muted-foreground">Ringkasan aktivitas kas — {ROLE_LABELS[user?.role]}</p>
        </div>
        <div className="flex gap-2">
          {canFilterBranch && (
            <select value={branch} onChange={(e) => setBranch(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="dashboard-branch-filter">
              <option value="all">Semua Cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {canExport && (
            <ExportDialog />
          )}
          {canRequestCash && (
            <Button onClick={() => navigate("/requests/new")} className="bg-brand hover:bg-emerald-800" data-testid="dashboard-new-request-btn">
              <FileText className="h-4 w-4 mr-2" /> Ajukan Kas
            </Button>
          )}
        </div>
      </div>

      {isFinance && <DashboardDepositSummary branch={canFilterBranch ? branch : "all"} />}
      {["it_admin", "direktur", "tim_keuangan", "staf_tagihan"].includes(user?.role) && <CompanyBillsPanel />}

      {user?.role === "pemohon" && balance && balance.outstanding > 0 && (
        <div data-testid="outstanding-warning-banner" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Anda memiliki saldo kas belum dipertanggungjawabkan sebesar {formatRupiah(balance.outstanding)}.</p>
            <p className="text-amber-700">Selesaikan upload bukti pengeluaran sebelum mengajukan permintaan kas baru.</p>
          </div>
        </div>
      )}

      {!pemohonSales && user?.role !== "staf_tagihan" && statGrid}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className={`${rightCol ? "lg:col-span-2" : "lg:col-span-3"} space-y-4`}>
          {!pemohonSales && user?.role !== "staf_tagihan" && <ActionNeeded />}
          {pemohonSales && <PemohonRevenuePanel />}
          {pemohonSales && <ReceivablesDuePanel />}
          {pemohonSales && <div data-testid="pemohon-bottom-summary" className="space-y-4">{statGrid}<ActionNeeded /></div>}
          {user?.role === "tim_keuangan" && <CashBalancesPanel />}

      <Tabs defaultValue={defaultTab} className="space-y-3" data-testid="dashboard-detail-tabs">
        <TabsList className="h-auto bg-secondary/80 flex-wrap justify-start">
          {tabs.map((t) => <TabsTrigger key={t.v} value={t.v} className="text-xs" data-testid={`dash-tab-${t.v}`}>{t.l}</TabsTrigger>)}
        </TabsList>
      <TabsContent value="pengajuan" className="mt-0">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pending list */}
        <Card className="lg:col-span-2 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Pengajuan Menunggu Approval</h3>
            <button onClick={() => navigate("/requests")} className="text-xs text-brand hover:underline flex items-center gap-1">
              Lihat semua <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Tidak ada pengajuan menunggu approval.</p>
          ) : (
            <div className="space-y-2">
              {pending.slice(0, 4).map((r) => (
                <button key={r.id} onClick={() => navigate(`/requests/${r.id}`)}
                  data-testid={`pending-item-${r.id}`}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg border hover:border-brand hover:bg-secondary/50 transition-colors text-left">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.requester_name} · {r.divisi}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.reason}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold font-mono">{formatRupiah(r.amount_requested)}</p>
                    <p className="text-[11px] text-muted-foreground">{formatDate(r.created_at)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Outstanding per requester */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-brand" />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Kas Berjalan per Pemohon</h3>
          </div>
          {(!stats || stats.outstanding_by_requester.length === 0) ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Semua kas sudah dipertanggungjawabkan.</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {stats.outstanding_by_requester.map((o) => (
                <div key={o.requester_id} data-testid={`outstanding-${o.requester_id}`} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{o.requester_name}</p>
                    <p className="text-xs text-muted-foreground">{o.divisi}</p>
                  </div>
                  <span className="text-sm font-semibold font-mono text-rose-600">{formatRupiah(o.outstanding)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      </TabsContent>

      {showBranch && (
        <TabsContent value="cabang" className="mt-0">
        <Card className="p-4" data-testid="branch-summary-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-brand" />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Rekap Kas per Cabang</h3>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left py-2">Cabang</th>
                  <th className="text-right py-2">Dicairkan</th>
                  <th className="text-right py-2">Dipertanggungjawabkan</th>
                  <th className="text-right py-2">Sisa Berjalan</th>
                </tr>
              </thead>
              <tbody>
                {stats.branch_summary.map((b, i) => (
                  <tr key={b.branch_name} className="border-t" data-testid={`branch-summary-row-${i}`}>
                    <td className="py-2 font-medium">{b.branch_name}</td>
                    <td className="py-2 text-right font-mono text-emerald-700">{formatRupiah(b.disbursed)}</td>
                    <td className="py-2 text-right font-mono">{formatRupiah(b.expensed)}</td>
                    <td className="py-2 text-right font-mono text-rose-600 font-semibold">{formatRupiah(b.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        </TabsContent>
      )}

      {showCredit && (
        <TabsContent value="kredit" className="mt-0">
        <Card className="p-4" data-testid="credit-summary-card">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-4 w-4 text-brand" />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Ringkasan Kredit Kendaraan</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="rounded-lg border p-4" data-testid="credit-due-month">
              <p className="text-xs text-muted-foreground">Cicilan Bulan Ini</p>
              <p className="text-xl font-bold font-mono text-brand mt-1">{formatRupiah(credit.total_due_this_month)}</p>
            </div>
            <div className="rounded-lg border p-4" data-testid="credit-count-kredit">
              <p className="text-xs text-muted-foreground">Kendaraan Kredit</p>
              <p className="text-xl font-bold mt-1">{credit.count_kredit}</p>
            </div>
            <div className="rounded-lg border p-4" data-testid="credit-count-lunas">
              <p className="text-xs text-muted-foreground">Sudah Lunas</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{credit.count_lunas}</p>
            </div>
          </div>
          {credit.monthly_burden?.some((m) => m.total > 0) && (
            <div className="mb-5" data-testid="credit-burden-chart">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Beban Cicilan 12 Bulan ke Depan</p>
              <div className="h-44 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={credit.monthly_burden.map((m) => ({ ...m, label: monthLabel(m.month) }))} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                      tickFormatter={(v) => v >= 1e9 ? `${(v / 1e9).toFixed(1)}M` : v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}rb` : v} width={44} />
                    <Tooltip formatter={(v) => [formatRupiah(v), "Total Cicilan"]} cursor={{ fill: "rgba(6,78,59,0.06)" }} />
                    <Bar dataKey="total" name="Total Cicilan" fill="#047857" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <p className="text-xs font-semibold text-muted-foreground mb-2">Jatuh Tempo Cicilan ≤ 7 Hari</p>
          {credit.due_7days.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3 text-center" data-testid="credit-due7-empty">Tidak ada cicilan jatuh tempo dalam 7 hari.</p>
          ) : (
            <div className="space-y-2">
              {credit.due_7days.map((v) => (
                <button key={v.vehicle_id} onClick={() => navigate(`/vehicles/${v.vehicle_id}`)}
                  data-testid={`credit-due-item-${v.vehicle_id}`}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border hover:border-brand hover:bg-secondary/50 transition-colors text-left">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{v.plate_number} · {v.vehicle_type}</p>
                    <p className="text-xs text-muted-foreground truncate">{v.lessor || v.branch}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold font-mono">{formatRupiah(v.monthly)}</p>
                    <p className={`text-[11px] ${v.days < 0 ? "text-rose-600" : "text-amber-600"}`}>{v.days < 0 ? `Terlambat ${Math.abs(v.days)} hr` : v.days === 0 ? "Hari ini" : `${v.days} hari lagi`}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
        </TabsContent>
      )}

      {showBills && <TabsContent value="tagihan" className="mt-0"><DashboardModules user={user} only="bills" /></TabsContent>}
      {showVeh && <TabsContent value="kendaraan" className="mt-0"><DashboardModules user={user} only="vehicles" /></TabsContent>}
      </Tabs>
        </div>
        {rightCol && (
          <div className="space-y-4">
            {showDonut && <VehicleStatusDonut />}
            {isFinance && <ExpenseSummaryPanel />}
          </div>
        )}
      </div>
    </div>
  );
}
